import { useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/lib/supabase";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { useCredentials, findCredentialByMarketplaceCode } from "@/lib/hooks/useCredentials";
import { useTenant } from "@/lib/hooks/useTenant";
import { BillingStatusCard } from "@/components/BillingStatusCard";
import { AliExpressCredentialSection } from "@/components/AliExpressCredentialSection";
import { MercadoLivreCredentialSection } from "@/components/MercadoLivreCredentialSection";
import { ShopeeCredentialSection } from "@/components/ShopeeCredentialSection";
import { GeminiCredentialSection } from "@/components/GeminiCredentialSection";
import { AmazonCredentialSection } from "@/components/credentials/AmazonCredentialSection";
import { MagaluCredentialSection } from "@/components/credentials/MagaluCredentialSection";
import { RakutenCredentialSection } from "@/components/credentials/RakutenCredentialSection";
import { SheinCredentialSection } from "@/components/credentials/SheinCredentialSection";
import { CredentialHealth } from "@/components/credentials/CredentialShell";
import { validationFeedback } from "@/lib/hooks/useCredentialSave";
import { RestartOnboardingCard } from "@/components/account/RestartOnboardingCard";
import { DangerZone } from "@/components/account/DangerZone";
import { useTelegramAccounts } from "@/lib/hooks/useTelegramAccounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  Bot,
  Building2,
  CheckCircle2,
  Copy,
  KeyRound,
  Link2,
  Loader2,
  LogOut,
  Send,
  ShieldCheck,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/PageHeader";

export function AccountPage(): JSX.Element {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();
  const { data: credentials = [], isLoading: loadingCredentials } = useCredentials();
  const { data: telegramAccounts = [], isLoading: loadingTelegramAccounts } = useTelegramAccounts();
  const { data: tenantBilling, isLoading: loadingTenant, isError: tenantError } = useTenant();
  const tenant = tenantBilling?.tenant ?? null;
  const subscription = tenantBilling?.subscription ?? null;
  const error = tenantError ? "Não foi possível carregar os dados do tenant." : null;
  const [telegramCode, setTelegramCode] = useState<string | null>(null);
  const [telegramError, setTelegramError] = useState<string | null>(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [awinPublisherId, setAwinPublisherId] = useState("");
  const [awinAdvertiserId, setAwinAdvertiserId] = useState("");
  const [awinAccessToken, setAwinAccessToken] = useState("");
  const [awinDomains, setAwinDomains] = useState("");
  const [awinCampaign, setAwinCampaign] = useState("");
  const [awinStatus, setAwinStatus] = useState<string | null>(null);
  const [awinError, setAwinError] = useState<string | null>(null);
  const [savingAwinCredential, setSavingAwinCredential] = useState(false);

  const amazonCredential = findCredentialByMarketplaceCode(credentials, "amazon");
  const mercadolivreCredential = findCredentialByMarketplaceCode(credentials, "mercadolivre");
  const aliexpressCredential = findCredentialByMarketplaceCode(credentials, "aliexpress");
  const shopeeCredential = findCredentialByMarketplaceCode(credentials, "shopee");
  const awinCredential = findCredentialByMarketplaceCode(credentials, "awin");
  const magaluCredential = findCredentialByMarketplaceCode(credentials, "magalu");
  const sheinCredential = findCredentialByMarketplaceCode(credentials, "shein");
  const rakutenCredential = credentials.find((credential) => credential.marketplace_code === "rakuten");
  const geminiCredential = credentials.find((credential) => credential.marketplace_code === "gemini_key");
  const telegramAccount = telegramAccounts.find((account) => account.verified_at);
  const telegramCommand = telegramCode ? `/start ${telegramCode}` : null;

  const handleSignOut = async (): Promise<void> => {
    await signOut();
  };

  const handleGenerateTelegramCode = async (): Promise<void> => {
    setGeneratingCode(true);
    setTelegramError(null);
    setCopiedCommand(false);

    const { data, error: rpcError } = await supabase.rpc("create_telegram_link_code");

    setGeneratingCode(false);

    if (rpcError || typeof data !== "string") {
      setTelegramCode(null);
      setTelegramError("Não foi possível gerar o código agora.");
      return;
    }

    setTelegramCode(data);
  };

  const handleCopyTelegramCommand = async (): Promise<void> => {
    if (!telegramCommand) return;

    await navigator.clipboard.writeText(telegramCommand);
    setCopiedCommand(true);
  };

  const handleSaveAwinCredential = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const normalizedPublisherId = awinPublisherId.trim();
    const normalizedAccessToken = awinAccessToken.trim();
    const normalizedAdvertiserId = Number(awinAdvertiserId.trim());
    const normalizedDomains = parseDomains(awinDomains);
    const normalizedCampaign = awinCampaign.trim() || null;

    setAwinStatus(null);
    setAwinError(null);

    if (!normalizedPublisherId || !normalizedAccessToken || !Number.isInteger(normalizedAdvertiserId)) {
      setAwinError("Preencha publisher ID, advertiser ID e token AWIN.");
      return;
    }

    if (normalizedDomains.length === 0) {
      setAwinError("Informe pelo menos um domínio do anunciante.");
      return;
    }

    setSavingAwinCredential(true);
    const { error: rpcError } = await supabase.rpc("upsert_awin_affiliate_credential", {
      p_publisher_id: normalizedPublisherId,
      p_access_token: normalizedAccessToken,
      p_advertiser_id: normalizedAdvertiserId,
      p_domains: normalizedDomains,
      p_campaign: normalizedCampaign,
    });
    setSavingAwinCredential(false);

    if (rpcError) {
      setAwinError(awinRpcErrorLabel(rpcError.message));
      return;
    }

    setAwinPublisherId("");
    setAwinAdvertiserId("");
    setAwinAccessToken("");
    setAwinDomains("");
    setAwinCampaign("");
    const feedback = await validationFeedback("awin");
    setAwinStatus(feedback.status);
    setAwinError(feedback.error);
    await queryClient.invalidateQueries({ queryKey: ["affiliate-credentials"] });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Cabecalho */}
      <PageHeader
        title="Minha conta"
        subtitle="Configure a empresa, o bot e as integrações por marketplace."
      />

      {/* Erro global */}
      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={15} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Secao: Dados do usuario */}
      <section
        className="rounded-xl border border-border bg-card overflow-hidden"
        aria-labelledby="section-usuario"
      >
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
          <User size={14} className="text-muted-foreground" />
          <h2
            id="section-usuario"
            className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            Usuário
          </h2>
        </div>
        <div className="p-5 space-y-4">
          <InfoRow label="E-mail" value={user?.email ?? "-"} mono />
        </div>
      </section>

      {/* Secao: Dados do tenant */}
      <section
        className="rounded-xl border border-border bg-card overflow-hidden"
        aria-labelledby="section-tenant"
      >
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
          <Building2 size={14} className="text-muted-foreground" />
          <h2
            id="section-tenant"
            className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            Organização
          </h2>
        </div>
        <div className="p-5">
          {loadingTenant && (
            <div className="space-y-3">
              <div className="h-4 w-32 animate-pulse rounded bg-muted" />
              <div className="h-4 w-20 animate-pulse rounded bg-muted/60" />
            </div>
          )}

          {!loadingTenant && tenant && (
            <div className="space-y-4">
              <InfoRow label="Nome" value={tenant.name} />
              <InfoRow
                label="Status"
                value={statusLabel(tenant.status)}
              />
            </div>
          )}

          {!loadingTenant && !tenant && !error && (
            <p className="text-sm text-muted-foreground">
              Nenhum tenant encontrado para esta conta.
            </p>
          )}
        </div>
      </section>

      {/* Secao: Plano e assinatura (Billing) */}
      {!loadingTenant && tenant && (
        <BillingStatusCard tenant={tenant} subscription={subscription} />
      )}

      {/* Secao: Telegram */}
      <section
        className="rounded-xl border border-border bg-card overflow-hidden"
        aria-labelledby="section-telegram"
      >
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
          <Bot size={14} className="text-muted-foreground" />
          <h2
            id="section-telegram"
            className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            Telegram
          </h2>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-foreground">Conta vinculada</p>
              {loadingTelegramAccounts && (
                <p className="text-sm text-muted-foreground">Carregando...</p>
              )}
              {!loadingTelegramAccounts && telegramAccount && (
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <ShieldCheck size={15} className="text-primary" />
                  <span className="font-mono">
                    {telegramAccount.username ? `@${telegramAccount.username}` : telegramAccount.telegram_user_id}
                  </span>
                  <span>verificada em {formatDate(telegramAccount.verified_at ?? telegramAccount.created_at)}</span>
                </div>
              )}
              {!loadingTelegramAccounts && !telegramAccount && (
                <p className="text-sm text-muted-foreground">Nenhuma conta Telegram vinculada.</p>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleGenerateTelegramCode}
              disabled={generatingCode}
            >
              {generatingCode ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Gerar código
            </Button>
          </div>

          {telegramError && (
            <InlineNotice tone="danger" icon={<AlertCircle size={15} />}>
              {telegramError}
            </InlineNotice>
          )}

          {telegramCommand && (
            <div className="rounded-lg border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Enviar ao @LucraLinkBot
                  </p>
                  <p className="font-mono text-sm text-foreground">{telegramCommand}</p>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  aria-label="Copiar comando"
                  onClick={handleCopyTelegramCommand}
                >
                  {copiedCommand ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                O código expira em 15 minutos.
              </p>
            </div>
          )}
        </div>
      </section>

      <RestartOnboardingCard />

      <GeminiCredentialSection
        credential={geminiCredential}
        loadingCredentials={loadingCredentials}
        plan={tenant?.plan ?? "free"}
        proUntil={tenant?.pro_until ?? null}
      />

      <AmazonCredentialSection credential={amazonCredential} />

      {/* Secao: AWIN */}
      <section
        id="platform-awin"
        className="rounded-xl border border-border bg-card overflow-hidden"
        aria-labelledby="section-awin"
      >
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
          <KeyRound size={14} className="text-muted-foreground" />
          <h2
            id="section-awin"
            className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            AWIN
          </h2>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-foreground">Credencial Link Builder</p>
              <p className="text-xs text-muted-foreground">
                Configuração principal de API com Publisher ID e Access Token.
              </p>
              {loadingCredentials && (
                <p className="text-sm text-muted-foreground">Carregando...</p>
              )}
              {!loadingCredentials && awinCredential && (
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <Link2 size={15} className="text-primary" />
                  <span className="font-mono">Publisher {awinCredential.public_id}</span>
                  <span className="font-mono">
                    Advertiser {credentialConfigValue(awinCredential, "advertiserId") ?? "-"}
                  </span>
                  <StatusBadge status={awinCredential.status} />
                </div>
              )}
              {!loadingCredentials && awinCredential && (
                <p className="text-xs text-muted-foreground font-mono">
                  {credentialConfigList(awinCredential, "domains").join(", ")}
                </p>
              )}
              {!loadingCredentials && !awinCredential && (
                <p className="text-sm text-muted-foreground">Nenhuma credencial AWIN salva.</p>
              )}
            </div>
          </div>

          <CredentialHealth credential={awinCredential} />

          <form onSubmit={handleSaveAwinCredential} className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="awin-publisher-id" variant="field">Publisher ID</Label>
              <Input
                id="awin-publisher-id"
                value={awinPublisherId}
                onChange={(event) => setAwinPublisherId(event.target.value)}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="awin-advertiser-id" variant="field">Advertiser ID</Label>
              <Input
                id="awin-advertiser-id"
                value={awinAdvertiserId}
                onChange={(event) => setAwinAdvertiserId(event.target.value)}
                placeholder="78910"
                inputMode="numeric"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="awin-token" variant="field">Access token</Label>
              <Input
                id="awin-token"
                type="password"
                value={awinAccessToken}
                onChange={(event) => setAwinAccessToken(event.target.value)}
                placeholder="eyJ..."
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="awin-campaign" variant="field">Campaign</Label>
              <Input
                id="awin-campaign"
                value={awinCampaign}
                onChange={(event) => setAwinCampaign(event.target.value)}
                placeholder="lucralink"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="awin-domains" variant="field">Domínios do anunciante</Label>
              <Input
                id="awin-domains"
                value={awinDomains}
                onChange={(event) => setAwinDomains(event.target.value)}
                placeholder="shein.com, br.shein.com"
                autoComplete="off"
              />
            </div>

            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" variant="gold" disabled={savingAwinCredential}>
                {savingAwinCredential ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                Salvar AWIN
              </Button>
            </div>
          </form>

          {awinStatus && (
            <InlineNotice tone="success" icon={<CheckCircle2 size={15} />}>
              {awinStatus}
            </InlineNotice>
          )}

          {awinError && (
            <InlineNotice tone="danger" icon={<AlertCircle size={15} />}>
              {awinError}
            </InlineNotice>
          )}
        </div>
      </section>

      <MercadoLivreCredentialSection
        credential={mercadolivreCredential}
        loadingCredentials={loadingCredentials}
      />

      <AliExpressCredentialSection
        credential={aliexpressCredential}
        loadingCredentials={loadingCredentials}
        sectionId="platform-aliexpress"
      />

      <ShopeeCredentialSection
        credential={shopeeCredential}
        loadingCredentials={loadingCredentials}
        sectionId="platform-shopee"
      />

      <RakutenCredentialSection credential={rakutenCredential} />

      <MagaluCredentialSection credential={magaluCredential} />

      <SheinCredentialSection credential={sheinCredential} />

      <DangerZone />

      {/* Acoes */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={handleSignOut}
          className="gap-2 text-destructive hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5"
        >
          <LogOut size={15} />
          Sair da conta
        </Button>
      </div>
    </div>
  );
}

function InlineNotice({
  children,
  icon,
  tone,
}: {
  children: ReactNode;
  icon: ReactNode;
  tone: "success" | "danger";
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "success"
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function InfoRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <p className={cn("text-sm font-medium text-foreground", mono && "font-mono")}>
        {value}
      </p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const isValid = status === "valid";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] border",
        isValid
          ? "bg-primary/10 text-primary border-primary/25"
          : "bg-muted text-muted-foreground border-transparent",
      )}
    >
      {isValid ? "válida" : statusLabel(status)}
    </span>
  );
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: "Ativo",
    inactive: "Inativo",
    suspended: "Suspenso",
    trial: "Trial",
  };
  return labels[status] ?? status;
}

function parseDomains(value: string): string[] {
  const domains = value
    .split(/[\n,]+/)
    .map(normalizeDomain)
    .filter(Boolean);

  return [...new Set(domains)];
}

function normalizeDomain(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) {
    return "";
  }

  try {
    const parsed = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    return parsed.hostname.replace(/\.$/, "");
  } catch {
    return trimmed.replace(/^https?:\/\//, "").split("/")[0]?.replace(/\.$/, "") ?? "";
  }
}

function credentialConfigValue(credential: AffiliateCredentialPublic, key: string): string | null {
  const value = credential.config_public?.[key];
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return null;
}

function credentialConfigList(credential: AffiliateCredentialPublic, key: string): string[] {
  const value = credential.config_public?.[key];
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string");
}

function awinRpcErrorLabel(message: string): string {
  const labels: Record<string, string> = {
    invalid_awin_publisher_id: "Publisher ID inválido.",
    invalid_awin_advertiser_id: "Advertiser ID inválido.",
    invalid_awin_access_token: "Access token inválido.",
    awin_domains_required: "Informe pelo menos um domínio do anunciante.",
    awin_domains_limit_exceeded: "Limite de domínios excedido.",
    invalid_awin_domain: "Domínio inválido.",
    tenant_not_found: "Tenant ativo não encontrado.",
  };

  for (const [code, label] of Object.entries(labels)) {
    if (message.includes(code)) {
      return label;
    }
  }

  return "Não foi possível salvar a credencial AWIN.";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
